<template>
    <div class="d-flex justify-space-between align-center">
        <UrlField
            :value="accessibilityUrl"
            :placeholder="$t('placeholder_url')"
            :rules="[formRules.maxLength(1000)]"
            @input="$emit('input', $event)"
        />
        <IconBtn
            v-if="!hideReloadBtn"
            :loading="state.loading"
            :disabled="state.loading"
            :title="$t('load_publication')"
            icon-cls="refresh"
            icon-color="primary"
            @click="onLoadAccessibilityData"
        />
        <v-dialog v-model="state.dialog" max-width="450" scrollable>
            <v-card>
                <v-card-title class="text-h5 primary white--text">{{ $t('pick_location') }}</v-card-title>
                <v-card-text style="height: 350px" class="px-0">
                    <v-container>
                        <v-row>
                            <v-col v-for="({ node }, i) in state.entriesBySearch.edges" :key="i" cols="12">
                                <v-hover v-slot="{ hover }">
                                    <v-card
                                        :elevation="hover ? 8 : 2"
                                        :class="{ 'on-hover': hover }"
                                        @click="
                                            $emit('input', node.publication.linkUrl);
                                            state.dialog = false;
                                        "
                                    >
                                        <v-card-title class="text-h5">
                                            {{ node.name }}
                                        </v-card-title>

                                        <v-card-subtitle>
                                            {{ node.position.city }} {{ node.position.postcode }}
                                        </v-card-subtitle>

                                        <v-card-text>
                                            <p>{{ node.position.street }} {{ node.position.housenumber }}</p>
                                        </v-card-text>
                                    </v-card>
                                </v-hover>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn text @click="state.dialog = false">{{ $t('cancel') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script setup lang="ts">
import { reactive } from 'vue';
import { instance as api } from '@/helpers/api';
import { commitToast } from '@/plugins/toast';
import { errToStr, formRules } from '@/helpers/tools';
import { LocationCollection } from '@/models/Location';
import IconBtn from '../IconBtn.vue';
import { getI18nInstance as i18n } from '@/i18n';

const props = defineProps({
    locationId: {
        type: Number,
        default: null,
    },
    accessibilityUrl: {
        type: String,
        default: null,
    },
    hideReloadBtn: {
        type: Boolean,
        default: false,
    },
});

const state = reactive({
    entriesBySearch: {
        edges: [
            {
                node: {
                    entryId: '',
                    name: '',
                    position: {
                        city: '',
                        postcode: '',
                        housenumber: '',
                        street: '',
                        lat: null,
                        lng: null,
                    },
                    publication: {
                        linkUrl: '',
                    },
                },
            },
        ],
    },
    loading: false,
    loaded: false,
    failed: false,
    dialog: false,
});

const emit = defineEmits(['input']);

async function onLoadAccessibilityData() {
    resetStates();

    if (!props.locationId) {
        commitToast({ message: `${i18n().t('no_location_to_look_for')}`, color: 'error' });
        return;
    }

    state.loading = true;
    try {
        const location = await loadLocationForId(props.locationId);
        if (location) {
            await apiCall(location.get('address_id'));
        }
    } catch (error) {
        commitToast({ message: errToStr(error), color: 'error' });
    }
    state.loading = false;
}

async function loadLocationForId(id: number) {
    if (!id) {
        return null;
    }
    const collection = new LocationCollection();
    await collection.query({ id, showAllLocations: true });
    return collection.first();
}

/*
Example response:
{
    "data": {
        "entriesBySearch": {
            "edges": [
                {
                    "node": {
                        "entryId": "a uuid",
                        "name": "Seemuseum Kreuzlingen",
                        "publication": {
                            "iconUrl": "https:\/\/www.ginto.guide\/entries\/2f038f5e-ea9d-40e1-bffc-09291f8599d5\/sticker.svg",
                            "linkUrl": "https:\/\/www.ginto.guide\/entries\/2f038f5e-ea9d-40e1-bffc-09291f8599d5?lnk_src=business",
                            "iconText": "OK:GO - Klicken Sie hier f\u00fcr Informationen zur Zug\u00e4nglichkeit",
                            "linkText": "Detaillierte Zug\u00e4nglichkeitsinfos"
                        },
                        "position": {
                            "city": "Kreuzlingen",
                            "countryCode": "CH",
                            "housenumber": "3",
                            "lat": 47.6487596834061,
                            "lng": 9.18933329931871,
                            "postcode": "8280",
                            "street": "Seeweg "
                        }
                    }
                }
            ]
        }
    },
    "success": true
}
*/
async function apiCall(addressId: number) {
    const res = await api().get('/Address/getAccessibilityData', {
        params: {
            addressId: addressId,
        },
    });

    state.entriesBySearch = res?.data?.data.entriesBySearch;
    const edges = state.entriesBySearch.edges ?? [];

    if (edges.length < 1) {
        state.failed = true;
        commitToast({ message: `${i18n().t('no_location_found')}`, color: 'error' });
    } else if (edges.length === 1) {
        emit('input', edges[0].node.publication.linkUrl);
        state.loaded = true;
    } else {
        state.loaded = true;
        state.dialog = true;
    }
}

function resetStates() {
    state.loaded = state.failed = state.dialog = false;
}
</script>

<style scoped lang="scss">
.col .v-card {
    transition: opacity 0.4s ease-in-out;

    &:not(.on-hover) {
        opacity: 0.6;
    }
}
</style>
